import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import contactsService, { PaginatedResponse } from '../services/contactsService';
import { Contact } from '../components/common/ContactForm';

// Function to get query options for a specific contact
export const getContactQueryOptions = (id: string) => ({
  queryKey: ['contact', id],
  queryFn: () => contactsService.getContact(id),
  enabled: !!id,
  staleTime: 1000 * 60 * 5, // 5 minutes
});

export interface UseContactsParams {
  searchQuery?: string;
  defaultOnly?: boolean;
  page?: number;
  per_page?: number;
  country_id?: number;
  enabled?: boolean;
}

// Main hook for contacts
export const useContacts = ({searchQuery, defaultOnly, page, per_page, country_id, enabled = true}: UseContactsParams) => {
  const queryClient = useQueryClient();
  
  // Fetch contacts list
  const { 
    data: response, 
    isLoading,
    error,
    isError,
    refetch // eslint-disable-line @typescript-eslint/no-unused-vars
  } = useQuery<PaginatedResponse<Contact>>({
    queryKey: ['contacts', searchQuery, defaultOnly, page, per_page, country_id],
    queryFn: () => {
      const params: any = {};
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      if (defaultOnly) {
        params.default = 1;
      }

      if (page) {
        params.page = page;
      }

      if (per_page) {
        params.per_page = per_page;
      }

      if (country_id) {
        params.country_id = country_id;
      }
      
      return contactsService.getContacts(params);
    },
    enabled,
  });
  
  // Extract contacts from the paginated response
  const contacts = response?.data || [];
  
  // Pagination meta data
  const pagination = response?.meta || null;
  
  // Add new contact
  const addContact = useMutation({
    mutationFn: contactsService.addContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  });
  
  // Update contact
  const updateContact = useMutation({
    mutationFn: contactsService.updateContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  });
  
  // Delete contact
  const deleteContact = useMutation({
    mutationFn: contactsService.deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  });

  // Refetch contacts
  const refetchContacts = () => {
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
  };

  const toggleFavorite = useMutation({
    mutationFn: contactsService.toggleFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  });
  
  const setAsDefault = useMutation({
    mutationFn: contactsService.setAsDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    }
  });
  
  return {
    refetch: refetchContacts,
    contacts,
    pagination,
    isLoading,
    error,
    isError,
    addContact: addContact.mutate,
    isPendingAdd: addContact.isPending,
    updateContact: updateContact.mutate,
    isPendingUpdate: updateContact.isPending,
    deleteContact: deleteContact.mutate,
    isPendingDelete: deleteContact.isPending,
    toggleFavorite: toggleFavorite.mutate,
    isPendingToggleFavorite: toggleFavorite.isPending,
    setAsDefault: setAsDefault.mutate,
    isPendingSetAsDefault: setAsDefault.isPending
  };
};

export default useContacts;

// Custom hook for a single contact
export const useContact = (id: string) => {
  return useQuery(getContactQueryOptions(id));
}; 
